import { supabaseUrl } from "../../../components/env";
import { getSupabase, supabase as globalSupabase } from "../../../components/supabaseClient";
import {
  clinicalTabValueToPlain,
  composeClinicalReportFromTabs,
} from "../../../utils/clinicalTabFormatting";
import {
  extractStorageObjectPathFromPublicUrl,
  extractVisitDateFromAudioStorageRef,
  guessCopilotJsonStoragePathCandidatesFromBackupAudioStoragePath,
  parseAudioIndexFromFilename,
} from "../../../utils/audioMetadata";

/** Keys returned by POST /api/generate-ai-recap/ mode clinical_only */
export const CLINICAL_TAB_LLM_KEYS = [
  "subjective",
  "ros",
  "pe",
  "objective",
  "assessment",
  "plan",
  "consider",
  "scores",
  "evidence",
  "report",
  "orders",
  "followups",
];

/** Same model as external recorder post-save (Sonnet). */
export const COPILOT_REGENERATE_MODEL_CHOICE = 1;

export const COPILOT_BUCKET_NAME = "copilot";

/**
 * Plain text from a patient `transcripts[]` row for AI clinical tabs / Copilot.
 * Handles Corti shapes: continuous_transcript, segment objects with text/content, etc.
 */
export function extractTranscriptPlainTextForClinicalAi(transcriptRow) {
  if (!transcriptRow || typeof transcriptRow !== "object") return "";

  const continuous = String(transcriptRow.continuous_transcript || "").trim();
  if (continuous) return continuous;

  const raw = transcriptRow.transcript;
  let fromSegments = "";
  if (raw != null) {
    if (Array.isArray(raw)) {
      fromSegments = raw
        .map((line) => {
          if (typeof line === "string") return line;
          if (line && typeof line === "object") {
            return (
              line.transcript ||
              line.text ||
              line.content ||
              line.value ||
              line.message ||
              ""
            );
          }
          return String(line || "");
        })
        .filter(Boolean)
        .join("\n");
    } else if (typeof raw === "object") {
      fromSegments = String(
        raw.transcript || raw.text || raw.content || "",
      ).trim();
    } else {
      fromSegments = String(raw).trim();
    }
  }
  if (fromSegments.trim()) return fromSegments.trim();

  for (const k of ["transcription_text", "transcribed_text", "full_transcript"]) {
    const v = String(transcriptRow[k] || "").trim();
    if (v) return v;
  }

  const soap = transcriptRow.soap_notes;
  if (soap && typeof soap === "object") {
    const sto = String(soap.transcript_only || "").trim();
    if (sto) return sto;
  }

  return "";
}

function clinicalTabsPayloadHasContent(tabs) {
  if (!tabs || typeof tabs !== "object") return false;
  return Object.values(tabs).some((v) => {
    if (v == null) return false;
    if (typeof v === "string") {
      const t = v.trim();
      if (!t) return false;
      if (t.toLowerCase() === "not discussed") return false;
      return true;
    }
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === "object") return Object.keys(v).length > 0;
    return String(v).trim().length > 0;
  });
}

/**
 * Fill missing LLM tab keys so regenerate never drops Report / Evidence UI sections.
 */
export function normalizeClinicalTabsFromApi(rawTabs) {
  const tabs = rawTabs && typeof rawTabs === "object" ? { ...rawTabs } : {};
  const out = {};

  for (const key of CLINICAL_TAB_LLM_KEYS) {
    const val = tabs[key];
    if (key === "evidence") {
      if (val == null || val === "") {
        out[key] = [];
      } else if (Array.isArray(val)) {
        out[key] = val
          .map((x) => clinicalTabValueToPlain(x))
          .filter((x) => x && String(x).trim());
      } else {
        const plain = clinicalTabValueToPlain(val);
        out[key] = plain && String(plain).trim() ? plain : [];
      }
      continue;
    }
    if (key === "orders" || key === "followups") {
      if (val == null || val === "") {
        out[key] = [];
      } else if (Array.isArray(val)) {
        out[key] = val
          .map((x) => {
            const raw = x && typeof x === "object" ? x.description || x.text || "" : x;
            return String(clinicalTabValueToPlain(raw) || "")
              .replace(/^\s*(?:\d+[.)]|[-*•])\s*/, "")
              .trim();
          })
          .filter(Boolean);
      } else {
        out[key] = String(clinicalTabValueToPlain(val) || "")
          .split("\n")
          .map((s) => s.replace(/^\s*(?:\d+[.)]|[-*•])\s*/, "").trim())
          .filter(Boolean);
      }
      continue;
    }
    if (key === "scores") {
      out[key] =
        val == null || val === "" ? "None mentioned" : val;
      continue;
    }
    if (key === "plan" && Array.isArray(val)) {
      const lines = val
        .map((x) => String(clinicalTabValueToPlain(x) || "").trim())
        .filter(Boolean)
        .slice(0, 10);
      out[key] = lines.length
        ? lines
            .map((line, i) =>
              /^(\d+\.|-|\*)\s/.test(line) ? line : `${i + 1}. ${line}`,
            )
            .join("\n")
        : "Not discussed";
      continue;
    }
    const plain = clinicalTabValueToPlain(val);
    out[key] =
      plain && String(plain).trim() ? String(plain).trim() : "Not discussed";
  }

  const composedReport = composeClinicalReportFromTabs(out);
  out.report = composedReport;
  out.report_plain = tabs.report_plain ?? composedReport;
  return out;
}

/**
 * When re-generating from transcript, keep Corti-only fields (facts, SOAP) from the prior snapshot.
 */
export function mergeCopilotClinicalTabsForRegenerate(existing, generated) {
  const merged = normalizeClinicalTabsFromApi(generated);
  const ex = existing && typeof existing === "object" ? existing : {};
  for (const k of ["facts", "soap_notes"]) {
    if (k === "facts" && Array.isArray(ex.facts) && ex.facts.length > 0) {
      merged.facts = ex.facts;
    }
    if (
      k === "soap_notes" &&
      ex.soap_notes &&
      typeof ex.soap_notes === "object" &&
      Object.keys(ex.soap_notes).length > 0
    ) {
      merged.soap_notes = ex.soap_notes;
    }
  }
  return merged;
}

const BACKUP_AUDIO_UUID_TAIL_RE =
  /_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Split `SanitizedName-EHR` audio stem prefix into save-copilot name fields (best-effort).
 * When there is no `-name/ehr` boundary, ehr is empty so the backend produces `Name-_AudioN_copilot.json`
 * (aligned with public-audio naming that omits a real EHR in the stem).
 */
function splitAudioStemPrefixToNameAndEhr(prefix) {
  const p = String(prefix || "").trim();
  if (!p) {
    return { patient_name: "unknown_patient", ehr_id: "" };
  }
  const li = p.lastIndexOf("-");
  if (li <= 0) {
    return { patient_name: p, ehr_id: "" };
  }
  return {
    patient_name: p.slice(0, li).trim() || "unknown_patient",
    ehr_id: p.slice(li + 1).trim() || "",
  };
}

/** First real EHR from row metadata (skip placeholders). Exported for Copilot fetch hints. */
export function copilotResolvedEhrHint(transcriptRow, audioRow) {
  const candidates = [
    transcriptRow?.ehr_id,
    transcriptRow?.ehrId,
    audioRow?.ehr_id,
    audioRow?.ehrId,
  ];
  for (const c of candidates) {
    if (c == null) continue;
    const s = String(c).trim();
    if (!s) continue;
    const low = s.toLowerCase();
    if (low === "unknown_ehr" || low === "unknown") continue;
    return s;
  }
  return "";
}

/** Never persist placeholder EHR in the storage filename. */
function normalizeEhrForCopilotFilename(ehr) {
  if (ehr == null) return "";
  const s = String(ehr).trim();
  if (!s) return "";
  const low = s.toLowerCase();
  if (low === "unknown_ehr" || low === "unknown") return "";
  return s;
}

function parseLeafStemFromObjectPath(objectPath) {
  const leaf = objectPath.split("/").filter(Boolean).pop() || "";
  const decoded = (() => {
    try {
      return decodeURIComponent(leaf);
    } catch {
      return leaf;
    }
  })();
  const stem = decoded.replace(/\.(webm|mp3|wav|m4a|ogg)$/i, "");
  const noUuid = stem.replace(BACKUP_AUDIO_UUID_TAIL_RE, "");
  return noUuid.replace(/%20/gi, " ").replace(/\s+/g, "_").replace(/_+/g, "_");
}

function toYyyyMmDd(raw) {
  if (raw == null || raw === "") return null;
  const s = String(raw).trim();
  const lead = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (lead) return lead[1];
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().split("T")[0];
}

/** schema/year/date from backup path or patient/transcript metadata (phone/legacy flat uploads). */
function resolveCopilotStorageFolder(objectPath, parts, audioRow, transcriptRow) {
  const dateKey = (seg) => {
    if (!seg) return null;
    const d = String(seg).split(/[T\s]/)[0];
    return /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : null;
  };
  if (parts.length >= 4 && /^\d{4}$/.test(parts[1]) && dateKey(parts[2])) {
    return { schema_name: parts[0], year: parts[1], date: dateKey(parts[2]) };
  }

  const schema_name =
    transcriptRow?.schema_name ||
    transcriptRow?.metadata?.schema_name ||
    audioRow?.schema_name ||
    audioRow?.metadata?.schema_name ||
    "public";

  const date =
    extractVisitDateFromAudioStorageRef(objectPath) ||
    extractVisitDateFromAudioStorageRef(
      audioRow?.storage_path || audioRow?.path || "",
    ) ||
    toYyyyMmDd(transcriptRow?.metadata?.date_of_service) ||
    toYyyyMmDd(transcriptRow?.metadata?.scheduled_date) ||
    toYyyyMmDd(transcriptRow?.metadata?.visit_date) ||
    toYyyyMmDd(transcriptRow?.metadata?.date_) ||
    toYyyyMmDd(audioRow?.recording_date) ||
    toYyyyMmDd(audioRow?.uploaded_at) ||
    toYyyyMmDd(audioRow?.created_at) ||
    toYyyyMmDd(transcriptRow?.created_at) ||
    toYyyyMmDd(new Date());

  if (!date) return null;
  return { schema_name, year: date.slice(0, 4), date };
}

function resolveTranscriptAudioSlot(transcriptRow) {
  const transcriptSlot =
    transcriptRow?.audio_index ??
    transcriptRow?.metadata?.audio_index ??
    transcriptRow?.metadata?.audioNumber ??
    transcriptRow?.metadata?.clinicalAudioSlot;
  const n =
    transcriptSlot != null && transcriptSlot !== ""
      ? Number(transcriptSlot)
      : NaN;
  if (!Number.isFinite(n) || n < 1) return null;
  return n;
}

/**
 * Infer audio_number and optional Name-EHR prefix from storage stem.
 * Supports backup-audio, external recorder, phone_audio, and legacy flat uploads.
 */
function resolveAudioStemIdentity(
  stem,
  transcriptSlotNum,
  audioRow,
  audioListIndexOneBased,
) {
  let directAudio = /^(.*)_Audio(\d+)_/i.exec(stem);
  if (!directAudio) {
    directAudio = /^(.*)_Audio(\d+)$/i.exec(stem);
  }
  if (directAudio) {
    return {
      nameEhrPrefix: directAudio[1],
      audio_number: parseInt(directAudio[2], 10) || 1,
    };
  }

  const backupStem = /^(.+)_Backup(\d+)$/i.exec(stem);
  if (backupStem) {
    return {
      nameEhrPrefix: backupStem[1],
      audio_number: parseInt(backupStem[2], 10) || 1,
    };
  }

  const rollingStem = /^(.+)_Rolling_.+$/i.exec(stem);
  if (rollingStem) {
    return {
      nameEhrPrefix: rollingStem[1],
      audio_number:
        transcriptSlotNum ?? parseAudioIndexFromFilename(stem) ?? 1,
    };
  }

  const recordingMatch = stem.match(/_Recording(\d+)/i);
  if (recordingMatch) {
    const prefix = stem.replace(/_Recording\d+.*/i, "");
    return {
      nameEhrPrefix: prefix || "",
      audio_number: parseInt(recordingMatch[1], 10) || 1,
    };
  }

  if (/^phone_audio_/i.test(stem) || audioRow?.source === "phone_audio") {
    return {
      nameEhrPrefix: "",
      audio_number:
        transcriptSlotNum ??
        parseAudioIndexFromFilename(stem) ??
        (Number.isFinite(audioListIndexOneBased) && audioListIndexOneBased >= 1
          ? audioListIndexOneBased
          : 1),
      identityFromRowsOnly: true,
    };
  }

  if (/^audio_/i.test(stem)) {
    return {
      nameEhrPrefix: "",
      audio_number:
        transcriptSlotNum ??
        parseAudioIndexFromFilename(stem) ??
        (Number.isFinite(audioListIndexOneBased) && audioListIndexOneBased >= 1
          ? audioListIndexOneBased
          : 1),
      identityFromRowsOnly: true,
    };
  }

  const fromFilename = parseAudioIndexFromFilename(stem);
  if (transcriptSlotNum != null) {
    return {
      nameEhrPrefix: "",
      audio_number: transcriptSlotNum,
      identityFromRowsOnly: true,
    };
  }
  if (fromFilename != null) {
    return {
      nameEhrPrefix: "",
      audio_number: fromFilename,
      identityFromRowsOnly: true,
    };
  }
  if (
    Number.isFinite(audioListIndexOneBased) &&
    audioListIndexOneBased >= 1
  ) {
    return {
      nameEhrPrefix: "",
      audio_number: audioListIndexOneBased,
      identityFromRowsOnly: true,
    };
  }

  return null;
}

/**
 * Build body for POST /api/transcription-backup/save-copilot/ from playback context.
 * Returns null when the URL is not a Supabase public object URL or visit folder cannot be resolved.
 *
 * @param {object} params
 * @param {object|null} params.audioRow
 * @param {object|null} params.transcriptRow
 * @param {string} params.audioPublicUrl
 * @param {object} params.clinical_tabs
 * @param {number} [params.audioListIndexOneBased] — 1-based index in patient more_audios list
 */
export function buildCopilotTabsBackupPayloadForPlayback({
  audioRow,
  transcriptRow,
  audioPublicUrl,
  clinical_tabs,
  audioListIndexOneBased,
}) {
  const objectPath = extractStorageObjectPathFromPublicUrl(audioPublicUrl);
  if (!objectPath) return null;
  const parts = objectPath.split("/").filter(Boolean);
  const folder = resolveCopilotStorageFolder(
    objectPath,
    parts,
    audioRow,
    transcriptRow,
  );
  if (!folder) return null;

  const { schema_name, year, date } = folder;
  const stem = parseLeafStemFromObjectPath(objectPath);
  const transcriptSlotNum = resolveTranscriptAudioSlot(transcriptRow);

  const stemIdentity = resolveAudioStemIdentity(
    stem,
    transcriptSlotNum,
    audioRow,
    audioListIndexOneBased,
  );
  if (!stemIdentity) return null;

  const { nameEhrPrefix, audio_number, identityFromRowsOnly } = stemIdentity;

  const realEhr = copilotResolvedEhrHint(transcriptRow, audioRow);

  let patient_name;
  let ehr_id;

  if (identityFromRowsOnly) {
    patient_name =
      transcriptRow?.patient_name ||
      audioRow?.patient_name ||
      audioRow?.patientName ||
      "unknown_patient";
    ehr_id = normalizeEhrForCopilotFilename(realEhr || "");
  } else {
    const workingPrefix = nameEhrPrefix.replace(/-unknown_ehr$/i, "");
    const hadUnknownEhrPlaceholder =
      workingPrefix.length < nameEhrPrefix.length;

    const { patient_name: parsedPatient, ehr_id: parsedEhrRaw } =
      splitAudioStemPrefixToNameAndEhr(workingPrefix);
    const parsedEhr = normalizeEhrForCopilotFilename(parsedEhrRaw);

    if (hadUnknownEhrPlaceholder) {
      patient_name =
        workingPrefix ||
        transcriptRow?.patient_name ||
        audioRow?.patient_name ||
        audioRow?.patientName ||
        parsedPatient;
      ehr_id = realEhr || parsedEhr || "";
    } else if (workingPrefix.lastIndexOf("-") > 0) {
      patient_name =
        transcriptRow?.patient_name ||
        audioRow?.patient_name ||
        audioRow?.patientName ||
        parsedPatient;
      ehr_id = realEhr || parsedEhr || "";
    } else {
      patient_name =
        transcriptRow?.patient_name ||
        audioRow?.patient_name ||
        audioRow?.patientName ||
        workingPrefix ||
        parsedPatient;
      ehr_id = realEhr || "";
    }

    ehr_id = normalizeEhrForCopilotFilename(ehr_id);
  }
  const session_id =
    (typeof transcriptRow?.session_id === "string" &&
      transcriptRow.session_id.trim()) ||
    (typeof transcriptRow?.metadata?.session_id === "string" &&
      transcriptRow.metadata.session_id.trim()) ||
    (typeof audioRow?.session_id === "string" && audioRow.session_id.trim()) ||
    objectPath;

  const patient_id = String(
    transcriptRow?.patient_id || audioRow?.patient_id || "unknown",
  );

  const tabs =
    clinical_tabs && typeof clinical_tabs === "object" ? { ...clinical_tabs } : {};
  if (tabs.report_plain == null && tabs.report != null) {
    tabs.report_plain = tabs.report;
  }

  return {
    patient_id,
    session_id,
    clinical_tabs: tabs,
    timestamp: new Date().toISOString(),
    schema_name,
    year,
    date,
    patient_name,
    ehr_id,
    audio_number,
    metadata: {
      source: "audio_playback_modal_generate",
    },
  };
}

/**
 * Run clinical-only AI generation and persist to copilot bucket (same API as external recorder).
 * @returns {Promise<{ ok: true }|{ ok: false, error: string }>}
 */
export async function generateClinicalTabsAndPersistCopilotSnapshot({
  transcriptText,
  backendBaseUrl,
  audioRow,
  transcriptRow,
  audioPublicUrl,
  existingClinicalTabs = null,
  modelChoice = COPILOT_REGENERATE_MODEL_CHOICE,
  audioListIndexOneBased,
  userId,
  userJwtToken,
  patientId,
  sessionId,
  schema,
}) {
  const text = String(transcriptText || "").trim();
  if (!text) {
    return { ok: false, error: "No transcript text to generate from." };
  }
  const base = String(backendBaseUrl || "").replace(/\/$/, "");
  if (!base) {
    return { ok: false, error: "Backend URL not configured." };
  }

  const schema_name =
    schema ||
    transcriptRow?.schema_name ||
    transcriptRow?.metadata?.schema_name ||
    audioRow?.schema_name ||
    audioRow?.metadata?.schema_name ||
    "public";

  const resolvedPatientId = patientId || transcriptRow?.patient_id || audioRow?.patient_id || "unknown";
  const resolvedSessionId = sessionId || transcriptRow?.session_id || audioRow?.session_id || `session_${resolvedPatientId}`;

  const genRes = await fetch(`${base}/api/generate-ai-recap/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      transcript: text,
      mode: "clinical_only",
      model_choice: modelChoice,
      userId,
      userJwtToken,
      patientId: resolvedPatientId,
      sessionId: resolvedSessionId,
      schema: schema_name,
    }),
  });
  const genJson = await genRes.json().catch(() => ({}));
  if (!genRes.ok || genJson.error) {
    return {
      ok: false,
      error:
        genJson.error ||
        (typeof genJson === "object" && genJson.detail) ||
        `Generation failed (${genRes.status})`,
    };
  }
  const rawTabs = genJson.clinical_tabs;
  if (!rawTabs || typeof rawTabs !== "object") {
    return { ok: false, error: "Model did not return clinical_tabs." };
  }

  const clinical_tabs = existingClinicalTabs
    ? mergeCopilotClinicalTabsForRegenerate(existingClinicalTabs, rawTabs)
    : normalizeClinicalTabsFromApi(rawTabs);

  if (!clinicalTabsPayloadHasContent(clinical_tabs)) {
    return {
      ok: false,
      error:
        "The model returned empty clinical tabs. Check that the transcript has readable text (Corti: continuous_transcript or per-line text).",
    };
  }

  const saveBody = buildCopilotTabsBackupPayloadForPlayback({
    audioRow,
    transcriptRow,
    audioPublicUrl,
    clinical_tabs,
    audioListIndexOneBased,
  });
  if (!saveBody) {
    const objectPath = extractStorageObjectPathFromPublicUrl(audioPublicUrl);
    let detail =
      "Use a Supabase storage URL for this recording, or re-upload the audio from the patient chart.";
    if (!objectPath) {
      detail =
        "This audio URL is not a Supabase public storage link (for example a pasted external URL).";
    } else if (
      !extractVisitDateFromAudioStorageRef(objectPath) &&
      !toYyyyMmDd(transcriptRow?.created_at || audioRow?.created_at)
    ) {
      detail =
        "Could not determine the visit date for this recording. Add a transcript with a date or use audio stored under schema/year/YYYY-MM-DD/.";
    }
    return {
      ok: false,
      error: `Could not infer Copilot storage path from this audio URL. ${detail}`,
    };
  }

  const saveRes = await fetch(`${base}/api/transcription-backup/save-copilot/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(saveBody),
  });
  const saveJson = await saveRes.json().catch(() => ({}));
  if (!saveRes.ok || !saveJson.success) {
    return {
      ok: false,
      error: saveJson.error || `Save to storage failed (${saveRes.status})`,
    };
  }

  return {
    ok: true,
    clinical_tabs,
    copilotRelativePath: saveJson.file_path || null,
    copilotFileUrl: saveJson.file_url || null,
  };
}

/**
 * Fetch copilot JSON at a path relative to the `copilot` bucket (schema/year/date/file_copilot.json).
 * @param {string} relativePath
 * @param {string} [storageOriginOverride] Unused — kept for call-site compatibility.
 *   It only fed the public-URL fallback, which is dead on a private bucket.
 * @returns {Promise<object|null>}
 */
export async function fetchCopilotSnapshotAtBucketRelativePath(
  relativePath,
  storageOriginOverride = "",
) {
  const rel = String(relativePath || "").replace(/^\/+/, "");
  if (!rel) return null;
  
  try {
    const supabase = await getSupabase();
    if (supabase) {
      const { data: signedData } = await supabase.storage
        .from(COPILOT_BUCKET_NAME)
        .createSignedUrl(rel, 3600);
        
      if (signedData?.signedUrl) {
        const res = await fetch(signedData.signedUrl);
        if (res.ok) return await res.json();
      }
    }
  } catch (err) {
    console.warn("⚠️ [Copilot] Failed to fetch relative copilot snapshot via signed URL:", err);
  }
  
  // No public-URL fallback: the copilot bucket is private, so /object/public/*
  // answers 400 whether or not the object is there. Authenticated download only.
  try {
    const supabase = await getSupabase();
    if (!supabase) return null;
    const { data, error } = await supabase.storage
      .from(COPILOT_BUCKET_NAME)
      .download(rel);
    if (!error && data) {
      const text = await data.text();
      return JSON.parse(text);
    }
  } catch {
    return null;
  }
  return null;
}

/**
 * Build the public-SHAPED pointer URL for a copilot object. This is an identifier
 * only — the bucket is private, so fetching it directly answers 400. Read the
 * object through a signed URL or `.download()` instead.
 */
export function buildPublicCopilotObjectUrl(pathRelative, storageOriginOverride = "") {
  const trimmedOverride = String(storageOriginOverride || "").replace(/\/$/, "");
  const base = trimmedOverride || String(supabaseUrl || "").replace(/\/$/, "");
  if (!base || !pathRelative) return "";
  const parts = String(pathRelative)
    .split("/")
    .filter(Boolean)
    .map((s) => encodeURIComponent(s));
  return `${base}/storage/v1/object/public/${COPILOT_BUCKET_NAME}/${parts.join("/")}`;
}

// A listing is capped; at the cap we cannot tell "absent" from "not on this page",
// so the probe falls back to trying each candidate rather than risk a false miss.
const COPILOT_LIST_LIMIT = 1000;

/**
 * Object names directly inside `dirname`, or null if the listing was unusable
 * (failed, or possibly truncated) and the caller should probe blindly instead.
 */
async function listCopilotFolderNames(dirname) {
  try {
    let supabase = await getSupabase();
    if (!supabase || !supabase.storage) supabase = globalSupabase;
    if (!supabase || !supabase.storage) return null;
    const { data, error } = await supabase.storage
      .from(COPILOT_BUCKET_NAME)
      .list(dirname, { limit: COPILOT_LIST_LIMIT });
    if (error || !Array.isArray(data)) return null;
    if (data.length >= COPILOT_LIST_LIMIT) return null;
    return data.map((f) => f && f.name).filter(Boolean);
  } catch {
    return null;
  }
}

/**
 * Load copilot JSON for a saved audio public URL (same path convention as transcripts).
 * @param {string} audioUrl
 * @param {{ resolvedCopilotEhr?: string }} [fetchOptions]
 * @returns {Promise<object|null>}
 */
export async function fetchCopilotSnapshotForAudioPublicUrl(
  audioUrl,
  fetchOptions = {},
) {
  const objectPath = extractStorageObjectPathFromPublicUrl(audioUrl);
  if (!objectPath) return null;
  const leaf = objectPath.split("/").pop() || "";
  const resolvedEhr =
    fetchOptions.resolvedCopilotEhr != null
      ? String(fetchOptions.resolvedCopilotEhr)
      : "";
  const hints = {
    clinicalAudioSlot: parseAudioIndexFromFilename(leaf),
    resolvedCopilotEhr: resolvedEhr,
  };
  let rels = guessCopilotJsonStoragePathCandidatesFromBackupAudioStoragePath(
    objectPath,
    hints,
  );
  if (!rels.length) return null;

  // Every candidate lives in the same folder, so one listing settles all of them.
  // Most audio is uploaded rather than copilot-recorded and has no sidecar at all;
  // without this the probe fires a doomed request per candidate and fills the
  // console with errors during an ordinary playback.
  const firstSlash = rels[0].lastIndexOf("/");
  const names = firstSlash > 0 ? await listCopilotFolderNames(rels[0].slice(0, firstSlash)) : null;
  if (names) {
    const present = new Set(names);
    rels = rels.filter((rel) => present.has(rel.slice(rel.lastIndexOf("/") + 1)));
    if (!rels.length) return null; // no copilot snapshot for this audio — normal
  }

  for (const rel of rels) {
    try {
      let supabase = await getSupabase();
      if (!supabase || !supabase.storage) {
        supabase = globalSupabase;
      }
      if (supabase && supabase.storage) {
        const { data: signedData } = await supabase.storage
          .from(COPILOT_BUCKET_NAME)
          .createSignedUrl(rel, 3600);
        
        if (signedData?.signedUrl) {
          const res = await fetch(signedData.signedUrl);
          if (res.ok) return await res.json();
        }
      }
    } catch (err) {
      console.warn("⚠️ [Copilot] Failed to fetch candidate copilot snapshot via signed URL:", err);
    }
    
    // No public-URL fallback here either — see buildPublicCopilotObjectUrl.
    try {
      let supabase = await getSupabase();
      if (!supabase || !supabase.storage) {
        supabase = globalSupabase;
      }
      if (!supabase || !supabase.storage) continue;
      const { data, error } = await supabase.storage
        .from(COPILOT_BUCKET_NAME)
        .download(rel);
      if (!error && data) {
        const text = await data.text();
        return JSON.parse(text);
      }
    } catch {
      /* try next */
    }
  }
  return null;
}
